import { type LoadingTask, type LoadingBarSnapshot } from './LoadingBar.types';

type Listener = () => void;

const EMPTY_SNAPSHOT: LoadingBarSnapshot = {
    progress: 0,
    isActive: false,
    isComplete: false,
    taskCount: 0,
    completedCount: 0,
};

export class LoadingBarManager {
    private tasks = new Map<string, LoadingTask>();

    private listeners = new Set<Listener>();

    private cachedSnapshot: LoadingBarSnapshot = EMPTY_SNAPSHOT;

    private snapshotDirty = true;

    register(id: string, options?: { weight?: number }): void {
        const existing = this.tasks.get(id);

        if (existing && existing.status === 'pending') {
            return;
        }

        this.tasks.set(id, {
            id,
            status: 'pending',
            progress: 0,
            weight: options?.weight ?? 1,
        });

        this.invalidateAndNotify();
    }

    updateProgress(id: string, progress: number): void {
        const task = this.tasks.get(id);

        if (!task || task.status === 'completed' || task.status === 'error') {
            return;
        }

        task.status = 'loading';
        task.progress = Math.min(100, Math.max(0, progress));

        this.invalidateAndNotify();
    }

    complete(id: string): void {
        const task = this.tasks.get(id);

        if (!task) {
            return;
        }

        task.status = 'completed';
        task.progress = 100;

        this.invalidateAndNotify();
    }

    fail(id: string): void {
        const task = this.tasks.get(id);

        if (!task) {
            return;
        }

        task.status = 'error';
        task.progress = 100;

        this.invalidateAndNotify();
    }

    reset(): void {
        this.tasks.clear();
        this.cachedSnapshot = EMPTY_SNAPSHOT;
        this.snapshotDirty = false;
        this.notify();
    }

    getSnapshot = (): LoadingBarSnapshot => {
        if (this.snapshotDirty) {
            this.cachedSnapshot = this.computeSnapshot();
            this.snapshotDirty = false;
        }

        return this.cachedSnapshot;
    };

    subscribe = (listener: Listener): (() => void) => {
        this.listeners.add(listener);

        return () => {
            this.listeners.delete(listener);
        };
    };

    private computeSnapshot(): LoadingBarSnapshot {
        const taskCount = this.tasks.size;

        if (taskCount === 0) {
            return EMPTY_SNAPSHOT;
        }

        let totalWeight = 0;
        let weightedProgress = 0;
        let completedCount = 0;

        this.tasks.forEach((task) => {
            totalWeight += task.weight;
            weightedProgress += task.progress * task.weight;

            if (task.status === 'completed' || task.status === 'error') {
                completedCount++;
            }
        });

        const progress = totalWeight > 0 ? (weightedProgress / (totalWeight * 100)) * 100 : 0;

        return {
            progress,
            isActive: true,
            isComplete: completedCount === taskCount,
            taskCount,
            completedCount,
        };
    }

    private invalidateAndNotify(): void {
        this.snapshotDirty = true;
        this.notify();
    }

    private notify(): void {
        this.listeners.forEach((listener) => listener());
    }
}

// Module-level accessor for non-React code (Redux thunks)
let activeManager: LoadingBarManager | null = null;

export function setActiveLoadingBarManager(manager: LoadingBarManager | null): void {
    activeManager = manager;
}

export function getActiveLoadingBarManager(): LoadingBarManager | null {
    return activeManager;
}
